import RangeFilter from "@/inside/models/RangeFilter";
import ValueFilter from "@/inside/models/ValueFilter";
import SeriesFilter from "@/inside/models/SeriesFilter";
import Assessment from "@/inside/models/Assessment/Assessment";

const combinations = {
    nofinished: [
        new RangeFilter("watch.status.assessment", [0, 99]),
        new SeriesFilter("watch.status.relevant", [Assessment.statusNegative]),
        new SeriesFilter("watch.status.compliant", [Assessment.statusPositive, Assessment.statusNegative]),
        new ValueFilter("text.isInformational", false)
    ],
    noconformity: [
        new SeriesFilter("watch.status.compliant", [Assessment.statusUnset, Assessment.statusPositive, Assessment.statusUnsure]),
        new SeriesFilter("watch.status.relevant", [Assessment.statusUnset, Assessment.statusNegative, Assessment.statusUnsure]),
        new ValueFilter("text.isInformational", false)
    ],
    new: [
        new ValueFilter("watch.status.text", "new")
    ],
    amended: [
        new ValueFilter("watch.status.text", "amended"),
        new ValueFilter("watch.status.requirement.isAmended", true)
    ],
    reassessment: [
        new ValueFilter("watch.status.reassessment", true),
        new ValueFilter("text.isInformational", false)
    ]
};

const validFilters = {
    "global.sites.id": {
        model: SeriesFilter,
        tags: ["global", "sites", "id"]
    },
    "watch.categories.id": {
        model: SeriesFilter,
        tags: ["watch", "categories", "id"]
    },
    "watch.texttypes.id": {
        model: SeriesFilter,
        tags: ["watch", "texttypes", "id"]
    },
    "watch.textdates.signature": {
        model: RangeFilter,
        tags: ["watch", "textdates", "signature"]
    },
    "watch.textdates.amendment": {
        model: RangeFilter,
        tags: ["watch", "textdates", "amendment"]
    },
    "watch.textdates.reassessment": {
        model: RangeFilter,
        tags: ["watch", "textdates", "reassessment"]
    },
    "watch.status.assessment": {
        model: RangeFilter,
        tags: ["watch", "status", "assessment"]
    },
    "watch.status.compliance": {
        model: RangeFilter,
        tags: ["watch", "status", "compliance"]
    },
    "watch.status.isViewed": {
        model: ValueFilter,
        tags: ["watch", "status", "isViewed"]
    },
    "watch.status.local": {
        model: ValueFilter,
        tags: ["watch", "status", "local"]
    },
    "watch.status.text": {
        model: ValueFilter,
        tags: ["watch", "status", "text"]
    },
    "watch.status.relevant": {
        model: SeriesFilter,
        tags: ["watch", "status", "relevant"]
    },
    "watch.status.compliant": {
        model: SeriesFilter,
        tags: ["watch", "status", "compliant"]
    },
    "watch.status.reassessment": {
        model: ValueFilter,
        tags: ["watch", "status", "reassessment"]
    },
    "watch.status.isLinkedToAction": {
        model: ValueFilter,
        tags: ["watch", "status", "isLinkedToAction"]
    },
    "watch.status.requirement.isAmended": {
        model: ValueFilter,
        tags: ["watch", "status", "requirement", "isAmended"]
    },
    "text.isAssessable": {
        model: ValueFilter,
        tags: ["text", "isAssessable"]
    },
    "text.isInformational": {
        model: ValueFilter,
        tags: ["text", "isInformational"]
    },
    "text.isAbrogated": {
        model: ValueFilter,
        tags: ["text", "isAbrogated"]
    },
    "search.watch": {
        model: ValueFilter,
        tags: ["search", "watch"]
    },
    "search.actions": {
        model: ValueFilter,
        tags: ["search", "actions"]
    },
    "status.actions": {
        model: 'SeriesFilter',
        tags: ["status", "actions"]
    },
    "actions.priority.value": {
        model: SeriesFilter,
        tags: ["actions", "priority"]
    },
    "actions.actiondates.due": {
        model: RangeFilter,
        tags: ["actions", "actionsdates", "due"]
    },
    "actions.tags.id": {
        model: SeriesFilter,
        tags: ["actions"]
    }
};

function computeActiveCombination(combinations, filters) {
    const filteredFilters = filters.filter((filter) => {
        return filter.field.split(".")[0] === "global"
        || filter.field.split(".")[0] === "watch"
    });

    if (filteredFilters.length === 0) {
        return null;
    }

    combinationsLoop: for (let combinationIdentifier in combinations) {
        const combinationFilters = combinations[combinationIdentifier].reduce((accumulator, filter) => {
            (Object.prototype.hasOwnProperty.call(accumulator, filter.field)
                ? accumulator[filter.field][filter instanceof SeriesFilter ? "add" : "set"](filter.value)
                : accumulator[filter.field] = filter
            );

            return accumulator;
        }, {});

        if (Object.keys(combinationFilters).length !== filteredFilters.length) {
            continue;
        }

        for (let i = 0; i < filteredFilters.length; i++) {
            const filter = filteredFilters[i];

            if (!Object.prototype.hasOwnProperty.call(combinationFilters, filter.field)) {
                continue combinationsLoop;
            }

            const combinationFilter = combinationFilters[filter.field];

            if (
                filter instanceof ValueFilter
                && filter.value !== combinationFilter.value
            ) {
                continue combinationsLoop;
            }

            if (
                filter instanceof RangeFilter
                && (
                    filter.value[0] !== combinationFilter.value[0]
                    || filter.value[1] !== combinationFilter.value[1]
                )
            ) {
                continue combinationsLoop;
            }

            if (filter instanceof SeriesFilter) {
                const remainingValues = filter.value.filter((existingValueFilter) => {
                    return !combinationFilter.value.includes(existingValueFilter);
                });

                if (remainingValues.length > 0) {
                    continue combinationsLoop;
                }
            }
        }

        return combinationIdentifier;
    }

    return "custom";
}

export default {
    actions: {
        /**
         * @param {function} commit
         * @param {object} state
         * @param {string} combination_identifier
         */
        applyCombination({commit, state}, combination_identifier) {
            if (combination_identifier === "custom") {
                commit("toggleDrawer", {
                    open: true,
                    mode: "filter"
                });
            }

            const filterSearch = state.filters.find((filter) => {
                return filter.field === "search.watch";
            });

            commit("reset");

            if (!Object.prototype.hasOwnProperty.call(combinations, combination_identifier)) {
                throw new Error("Invalid combination identifier");
            }

            combinations[combination_identifier].forEach((filter) => {
                commit("exclude", filter);
            });

            if (filterSearch !== undefined) {
                commit("exclude", filterSearch);
            }

            state.activeCombination = combination_identifier;
        }
    },
    getters: {
        /**
         * Returns active combination identifier currently stored in module state.
         *
         * @param {object} state
         * @return {string|null}
         */
        activeCombination(state) {
            return state.activeCombination;
        },
        /**
         * Returns all filters currently stored in module state.
         *
         * @param {object} state
         * @return {Array}
         */
        all(state) {
            return state.filters;
        },
        /**
         * Returns drawer structure currently stored in module state.
         *
         * @param {object} state
         * @return {Array}
         */
        drawerStructure(state){
            return state.drawerStructure;
        },
        /**
         * Returns a boolean indicating whether or not given `filter` value is "included".
         *
         * @param {object} state
         * @return {function(object): boolean}
         */
        isIncluded(state) {
            return (filter) => {
                let filterExcluded = state.filters.find((filter_existing) => {
                    return filter_existing.field === filter.field
                })

                if(filter instanceof SeriesFilter){
                    return (
                        filterExcluded === undefined
                        || filterExcluded.value.find((value) => {
                            return filter.value[0] === value;
                        }) === undefined
                    )
                }

                if(filter instanceof ValueFilter){
                    return (
                        filterExcluded === undefined
                        || filterExcluded.value !== filter.value
                    )
                }

                if(filter instanceof RangeFilter){
                    return (
                        filterExcluded === undefined
                        || (
                            filterExcluded.value[0] !== filter.value[0]
                            || filterExcluded.value[1] !== filter.value[1]
                        )
                    )
                }
            }
        },
        /**
         * Returns any filter corresponding with given `filter_field`.
         *
         * @param {object} state
         * @return {function(string): SeriesFilter|RangeFilter|ValueFilter|undefined}
         */
        one(state) {
            return (filter_field) => {
                return state.filters.find((filter) => {
                    return filter.field === filter_field;
                });
            };
        }
    },
    mutations: {
        /**
         * @param {object} state
         * @param {object} filter
         */
        exclude(state, filter) {
            if (Object.prototype.hasOwnProperty.call(validFilters, filter.field) === false) {
                throw new Error(`The filter ${filter.field} is invalid`);
            }

            let index = state.filters.findIndex((filter_existing) => {
                return filter_existing.field === filter.field
            });

            if (index === -1) {
                state.filters.push(filter instanceof SeriesFilter
                    ? new SeriesFilter(filter.field, filter.value)
                    : filter
                );

                state.activeCombination = computeActiveCombination(combinations, state.filters);
                return;
            }

            const existingFilter = state.filters[index];

            if(existingFilter instanceof SeriesFilter){
                existingFilter.add(filter.value)
            }
            else if(existingFilter instanceof RangeFilter){
                existingFilter.setMin(filter.value[0]);
                existingFilter.setMax(filter.value[1]);

            }
            else{
                existingFilter.set(filter.value)
            }

            state.activeCombination = computeActiveCombination(combinations, state.filters);
        },
        /**
         *
         * @param {object} state
         * @param {object} filter
         */
        include(state, filter) {
            if (Object.prototype.hasOwnProperty.call(validFilters, filter.field) === false) {
                throw new Error(`The filter ${filter.field} is invalid`);
            }

            let index = state.filters.findIndex((filter_existing) => {
                return filter_existing.field === filter.field
            });

            if (index === -1) {
                return;
            }

            const existingFilter = state.filters[index];

            if (existingFilter instanceof SeriesFilter) {
                existingFilter.remove(filter.value);

                if (existingFilter.value.length > 0) {
                    state.activeCombination = computeActiveCombination(combinations, state.filters);
                    return;
                }
            }

            state.filters.splice(index, 1);
            state.activeCombination = computeActiveCombination(combinations, state.filters);
        },
        /**
         * @param {Object} state
         */
        reset(state) {
            state.filters = [];
            state.activeCombination = null;
        },
        /**
         * Stores given `structure` in module state.
         *
         * @param {object} state
         * @param {Array} structure
         */
        setDrawerStructure(state, structure) {
            if (!Array.isArray(structure)) {
                throw new TypeError(`VueX 'filters.setDrawerStructure' mutation expects payload to be an array, ${typeof structure} given`);
            }

            state.drawerStructure = structure;
        }
    },
    namespaced: true,
    state: {
        activeCombination: null,
        drawerStructure: [],
        filters: []
    }
}
